import { Button, Card, CardBody, CardHeader, useToast } from '@ambernord/ui';
import { AlertTriangle, CheckCircle2, Circle, FileText, Upload, XCircle } from 'lucide-react';
import { type ChangeEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import {
  ARTIFACT_KINDS,
  type ArtifactKind,
  type ArtifactsDto,
  type ShippingDocItem,
} from './types.js';

/** Ģenerējamie dokumenti → shipping-docs galapunkts. Pārējie divi ir obligātie skenējumi. */
const GENERATE_ENDPOINT: Partial<Record<ArtifactKind, string>> = {
  label: '/api/shipping-docs/label',
  cmr: '/api/shipping-docs/cmr',
  packing_list: '/api/shipping-docs/packing-list',
  delivery_note: '/api/shipping-docs/delivery-note',
};

/**
 * Dokumentu & artefaktu kontrolsaraksts: apvieno GET /:id/artifacts (trūkstošie/vārtu
 * brīdinājums) ar GET /api/shipping-docs/supply-order/:id (klātesošie — veids tiek
 * atpazīts pēc R2 atslēgas prefiksa `so/{id}/artifact/{kind}/`).
 */
export function ArtifactChecklist({
  supplyOrderId,
  artifacts,
  docs,
  canGenerate,
  canUpload,
  onChanged,
}: {
  supplyOrderId: string;
  artifacts: ArtifactsDto | null;
  docs: ShippingDocItem[];
  canGenerate: boolean;
  canUpload: boolean;
  onChanged: () => void | Promise<void>;
}) {
  const { t, i18n } = useTranslation('sodetail');
  const { toast } = useToast();
  const [busyKind, setBusyKind] = useState<ArtifactKind | null>(null);
  const [scanKind, setScanKind] = useState<ArtifactKind | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const present = useMemo(() => {
    const set = new Set<ArtifactKind>();
    for (const doc of docs) {
      for (const kind of ARTIFACT_KINDS) {
        if (doc.fileR2Key.startsWith(`so/${supplyOrderId}/artifact/${kind}/`)) set.add(kind);
      }
    }
    return set;
  }, [docs, supplyOrderId]);

  const missing = useMemo(() => new Set(artifacts?.missing ?? []), [artifacts]);

  async function generate(kind: ArtifactKind) {
    const endpoint = GENERATE_ENDPOINT[kind];
    if (!endpoint) return;
    setBusyKind(kind);
    try {
      const res = await api.post<{ documentId?: string; error?: string }>(endpoint, {
        supplyOrderId,
        locale: i18n.language.slice(0, 2),
      });
      if (res.ok) {
        toast(t('artifacts.generated'));
        await onChanged();
      } else {
        toast(res.data.error ?? t('artifacts.error'), 'error');
      }
    } finally {
      setBusyKind(null);
    }
  }

  function pickScan(kind: ArtifactKind) {
    setScanKind(kind);
    fileRef.current?.click();
  }

  function onFileChosen(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const kind = scanKind;
    e.target.value = '';
    if (!file || !kind) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : '';
      const base64 = dataUrl.split(',')[1] ?? '';
      if (!base64) {
        toast(t('artifacts.error'), 'error');
        return;
      }
      void (async () => {
        setBusyKind(kind);
        try {
          const res = await api.post<{ scan?: unknown; error?: string }>(
            `/api/supply-orders/${supplyOrderId}/scan`,
            {
              scanType: kind,
              filename: file.name,
              contentBase64: base64,
              contentType: file.type || 'application/octet-stream',
            },
          );
          if (res.ok) {
            toast(t('artifacts.uploaded'));
            await onChanged();
          } else {
            toast(res.data.error ?? t('artifacts.error'), 'error');
          }
        } finally {
          setBusyKind(null);
        }
      })();
    };
    reader.readAsDataURL(file);
  }

  function rowIcon(kind: ArtifactKind) {
    if (present.has(kind))
      return <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" aria-hidden />;
    if (missing.has(kind)) return <XCircle className="h-5 w-5 shrink-0 text-red-500" aria-hidden />;
    return <Circle className="h-5 w-5 shrink-0 text-amber-500" aria-hidden />;
  }

  function rowState(kind: ArtifactKind): string {
    if (present.has(kind)) return t('artifacts.state.present');
    if (missing.has(kind)) return t('artifacts.state.missing');
    return t('artifacts.state.pending');
  }

  function rowAction(kind: ArtifactKind) {
    const busy = busyKind === kind;
    if (GENERATE_ENDPOINT[kind]) {
      if (!canGenerate) return null;
      return (
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => void generate(kind)}>
          <FileText className="h-4 w-4" aria-hidden />
          {present.has(kind) ? t('artifacts.regenerate') : t('artifacts.generate')}
        </Button>
      );
    }
    if (!canUpload) return null;
    return (
      <Button size="sm" variant="secondary" disabled={busy} onClick={() => pickScan(kind)}>
        <Upload className="h-4 w-4" aria-hidden />
        {busy ? t('artifacts.uploading') : t('artifacts.upload')}
      </Button>
    );
  }

  return (
    <Card id="artifacts">
      <CardHeader title={t('artifacts.title')} subtitle={t('artifacts.subtitle')} />
      <CardBody>
        {artifacts?.warning ? (
          <div
            className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
            role="alert"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              {t('artifacts.warning', {
                items: (artifacts.missing ?? []).map((k) => t(`artifacts.kinds.${k}`)).join(', '),
              })}
            </span>
          </div>
        ) : null}

        <ul className="divide-y divide-slate-100 dark:divide-slate-800/60">
          {ARTIFACT_KINDS.map((kind) => (
            <li key={kind} className="flex items-center justify-between gap-3 py-2.5">
              <div className="flex min-w-0 items-center gap-2.5">
                {rowIcon(kind)}
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                    {t(`artifacts.kinds.${kind}`)}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{rowState(kind)}</p>
                </div>
              </div>
              {rowAction(kind)}
            </li>
          ))}
        </ul>

        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,image/*"
          className="hidden"
          onChange={onFileChosen}
        />
      </CardBody>
    </Card>
  );
}
